import fetchData from "../../api/apiService";
import { buildTenantApiUrl } from "../../auth/utilities";
const BASE_ENDPOINT = "tprm/";

const buildQueryString = (params = {}) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length) {
        query.append(key, value.join(","));
      }
      return;
    }

    query.append(key, value);
  });

  const queryString = query.toString();
  return queryString ? `?${queryString}` : "";
};

const withQuery = (path, params = {}) => `${BASE_ENDPOINT}${path}${buildQueryString(params)}`;

const splitQueryPayload = (payload = {}, queryKeys = []) => {
  const queryParams = {};
  const body = { ...payload };

  queryKeys.forEach((key) => {
    if (body[key] !== undefined && body[key] !== null && body[key] !== "") {
      queryParams[key] = body[key];
    }
    delete body[key];
  });

  return { body, query: buildQueryString(queryParams) };
};

// Dashboard
export const getDashboardStats = async () => {
  return await fetchData(`${BASE_ENDPOINT}dashboard/`);
};

// Vendor CRUD
export const getVendorList = async (params = {}) => {
  return await fetchData(withQuery("vendors/", params));
};

export const createVendor = async (vendorData) => {
  return await fetchData(`${BASE_ENDPOINT}create-vendor/`, {
    method: "POST",
    body: vendorData,
  });
};

export const getVendorDetail = async (vendorId) => {
  return await fetchData(`${BASE_ENDPOINT}vendors/${vendorId}/`);
};

export const updateVendor = async (vendorId, vendorData) => {
  return await fetchData(`${BASE_ENDPOINT}vendors/${vendorId}/`, {
    method: "PUT",
    body: vendorData,
  });
};

export const updateVendorStatus = async (vendorId, vendorStatus) => {
  return await fetchData(`${BASE_ENDPOINT}vendors/${vendorId}/update-status/`, {
    method: "PUT",
    body: { vendor_status: vendorStatus },
  });
};

export const deleteVendor = async (vendorId) => {
  return await fetchData(`${BASE_ENDPOINT}vendors/${vendorId}/`, {
    method: "DELETE",
  });
};

// Vendor Assessment / Due Diligence
export const initiateVendorAssessment = async (vendorId, payload = {}) => {
  return await fetchData(`${BASE_ENDPOINT}initiate-vendor-assessment/${vendorId}/`, {
    method: "POST",
    body: payload,
  });
};

export const bulkInitiateVendorAssessment = async (payload = {}) => {
  return await fetchData(`${BASE_ENDPOINT}bulk-initiate-vendor-assessment/`, {
    method: "POST",
    body: payload,
  });
};

export const getVendorAssessmentSubcategories = async (params = {}) => {
  return await fetchData(withQuery("vendor-assessment-sub-categories/", params));
};

export const getVendorAssessmentQuestionnaire = async (params = {}) => {
  return await fetchData(withQuery("vendor-assessment-questionnaire/", params));
};

export const updateVendorAssessmentAnswer = async (answerData) => {
  const { body, query } = splitQueryPayload(answerData, ["token", "assessment_id"]);
  return await fetchData(`${BASE_ENDPOINT}update-vendor-assessment-answer/${query}`, {
    method: "PUT",
    body,
  });
};

export const saveVendorAssessmentAttachment = async (attachmentData) => {
  const { body, query } = splitQueryPayload(attachmentData, ["token", "assessment_id"]);
  return await fetchData(`${BASE_ENDPOINT}save-vendor-assessment-attachment/${query}`, {
    method: "POST",
    body,
  });
};

export const deleteVendorAssessmentAttachment = async (params) => {
  const { body, query } = splitQueryPayload(params, ["token", "assessment_id"]);
  return await fetchData(`${BASE_ENDPOINT}delete-vendor-assessment-attachment/${query}`, {
    method: "DELETE",
    body,
  });
};

export const submitVendorAssessment = async (assessmentData) => {
  const { body, query } = splitQueryPayload(assessmentData, ["token", "assessment_id"]);
  return await fetchData(`${BASE_ENDPOINT}submit-vendor-assessment/${query}`, {
    method: "POST",
    body,
  });
};

export const getVendorAssessmentDetail = async (params = {}) => {
  return await fetchData(withQuery("vendor-assessment-detail/", params));
};

export const getVendorAssessmentSubcategoryList = async (vendorId) => {
  return await fetchData(`${BASE_ENDPOINT}vendor-assessment-subcategory-list/${vendorId}/`);
};

// Custom Questionnaire Builder

export const createCustomSubcategory = async (subcategoryData) => {
  return await fetchData(`${BASE_ENDPOINT}custom-subcategory/`, {
    method: "POST",
    body: subcategoryData,
  });
};

export const createCustomSection = async (sectionData) => {
  return await fetchData(`${BASE_ENDPOINT}custom-section/`, {
    method: "POST",
    body: sectionData,
  });
};

export const createCustomQuestion = async (questionData) => {
  return await fetchData(`${BASE_ENDPOINT}custom-question/`, {
    method: "POST",
    body: questionData,
  });
};

// Questionnaire Template Builder
export const getQuestionnaireCategories = async (vendorId) => {
  const params = vendorId ? `?vendor_id=${vendorId}` : "";
  return await fetchData(`${BASE_ENDPOINT}questionnaire-categories/${params}`);
};

export const getQuestionnaireSubcategories = async (categories) => {
  return await fetchData(withQuery("questionnaire-subcategories/", { categories }));
};

export const getQuestionnaireSections = async (subcategory, type = "master") => {
  return await fetchData(withQuery("questionnaire-sections/", { subcategory, type }));
};

export const getQuestionnaireQuestions = async (section, type = "master", subcategory = "", category = "") => {
  const params = { section, type };
  if (subcategory) params.subcategory = subcategory;
  if (category) params.category = category;
  const queryParams = new URLSearchParams(params).toString();
  return await fetchData(`${BASE_ENDPOINT}questionnaire-questions/?${queryParams}`);
};

export const getQuestionnaireTemplates = async () => {
  return await fetchData(`${BASE_ENDPOINT}questionnaire-templates/`);
};

export const createQuestionnaireTemplate = async (templateData) => {
  return await fetchData(`${BASE_ENDPOINT}questionnaire-templates/`, {
    method: "POST",
    body: templateData,
  });
};

export const getQuestionnaireTemplateDetail = async (templateId) => {
  return await fetchData(`${BASE_ENDPOINT}questionnaire-templates/${templateId}/`);
};

export const deleteQuestionnaireTemplate = async (templateId) => {
  return await fetchData(`${BASE_ENDPOINT}questionnaire-templates/${templateId}/`, {
    method: "DELETE",
  });
};

export const duplicateQuestionnaireTemplate = async (templateId, templateName = "") => {
  return await fetchData(`${BASE_ENDPOINT}questionnaire-templates/${templateId}/duplicate/`, {
    method: "POST",
    body: templateName ? { template_name: templateName } : {},
  });
};

export const getDeletedQuestionnaireTemplates = async () => {
  return await fetchData(`${BASE_ENDPOINT}questionnaire-templates/deleted/`);
};

export const restoreQuestionnaireTemplate = async (templateId) => {
  return await fetchData(`${BASE_ENDPOINT}questionnaire-templates/${templateId}/restore/`, {
    method: "POST",
  });
};

export const permanentDeleteQuestionnaireTemplate = async (templateId) => {
  return await fetchData(`${BASE_ENDPOINT}questionnaire-templates/${templateId}/permanent-delete/`, {
    method: "DELETE",
  });
};

// Vendor Onboarding
export const getVendorOnboardQuestions = async () => {
  return await fetchData(`${BASE_ENDPOINT}vendor-onboard-questions/`);
};

export const createCustomVendorOnboard = async (questionData) => {
  return await fetchData(`${BASE_ENDPOINT}create-custom-vendor-onboard/`, {
    method: "POST",
    body: questionData,
  });
};

export const getCustomVendorOnboardQuestionDetail = async (questionId) => {
  return await fetchData(`${BASE_ENDPOINT}custom-vendor-onboard-question-detail/${questionId}/`);
};

export const updateCustomVendorOnboardQuestion = async (questionId, questionData) => {
  return await fetchData(`${BASE_ENDPOINT}custom-vendor-onboard-question-detail/${questionId}/`, {
    method: "PUT",
    body: questionData,
  });
};

export const deleteCustomVendorOnboardQuestion = async (questionId) => {
  return await fetchData(`${BASE_ENDPOINT}custom-vendor-onboard-question-detail/${questionId}/`, {
    method: "DELETE",
  });
};

export const submitVendorOnboarding = async (onboardingData) => {
  return await fetchData(`${BASE_ENDPOINT}vendor-onboarding-submit/`, {
    method: "POST",
    body: onboardingData,
  });
};

export const getVendorOnboardAssessmentAnswer = async (vendorId) => {
  return await fetchData(`${BASE_ENDPOINT}vendor-onboard-assessment-answer/${vendorId}/`);
};

export const requestVendorOnboardAssessment = async (requestData) => {
  return await fetchData(`${BASE_ENDPOINT}vendor-onboard-assessment-request-view/`, {
    method: "POST",
    body: requestData,
  });
};

export const getVendorOnboardAssessmentQuestionnaire = async (params = {}) => {
  return await fetchData(withQuery("vendor-onboard-assessment-questionnaire-view/", params));
};

export const updateVendorOnboardAnswer = async (answerData) => {
  const { body, query } = splitQueryPayload(answerData, ["token"]);
  return await fetchData(`${BASE_ENDPOINT}update-vendor-onboard-answer/${query}`, {
    method: "PUT",
    body,
  });
};

export const submitVendorOnboardAssessment = async (assessmentData) => {
  const { body, query } = splitQueryPayload(assessmentData, ["token"]);
  return await fetchData(`${BASE_ENDPOINT}vendor-onboard-assessment-submit/${query}`, {
    method: "POST",
    body,
  });
};



// Vendor Assessment Report
export const getVendorAssessmentReport = async (assessmentId) => {
  return await fetchData(`${BASE_ENDPOINT}vendor-assessment-report/${assessmentId}/`);
};

export const approveVendorAssessment = async (vendorRiskAssessmentId) => {
  return await fetchData(`${BASE_ENDPOINT}approve-vendor-assessment/`, {
    method: "POST",
    body: {
      vendor_risk_assessment_id: vendorRiskAssessmentId,
    },
  });
};

export const conditionalApproveVendor = async (vendorId, assessmentId, approvedAnswerIds, deadline) => {
  return await fetchData(`${BASE_ENDPOINT}conditional-approve-vendor/`, {
    method: "POST",
    body: {
      vendor_id: vendorId,
      assessment_id: assessmentId,
      approved_answer_ids: approvedAnswerIds,
      deadline: deadline,
    },
  });
};

// Vendor Risk Register
export const getVendorRiskRegisterList = async () => {
  return await fetchData(`${BASE_ENDPOINT}vendor-risk-register/`);
};

export const getVendorRiskRegisterDetail = async (vendorId) => {
  return await fetchData(`${BASE_ENDPOINT}vendor-risk-register/${vendorId}/`);
};

export const getVendorRiskRegisterByRisk = async () => {
  return await fetchData(`${BASE_ENDPOINT}vendor-risk-register-by-risk/`);
};

export const createCustomRisk = async (data) => {
  return await fetchData(`${BASE_ENDPOINT}custom-risk-register/`, {
    method: "POST",
    body: data,
  });
};

export const getCustomRiskDetail = async (riskId) => {
  return await fetchData(`${BASE_ENDPOINT}custom-risk-register/${riskId}/`);
};

export const updateCustomRisk = async (riskId, data) => {
  return await fetchData(`${BASE_ENDPOINT}custom-risk-register/${riskId}/`, {
    method: "PUT",
    body: data,
  });
};

export const getAssessmentRiskDetail = async (riskId) => {
  return await fetchData(`${BASE_ENDPOINT}assessment-risk-register/${riskId}/`);
};

export const updateAssessmentRisk = async (riskId, data) => {
  return await fetchData(`${BASE_ENDPOINT}assessment-risk-register/${riskId}/`, {
    method: "PUT",
    body: data,
  });
};

// ─── Risk Register Attachments ───

export const uploadRiskRegisterAttachment = async (riskId, riskType, files) => {
  const formData = new FormData();
  formData.append("risk_id", riskId);
  formData.append("risk_type", riskType);
  for (const file of files) {
    formData.append("file", file);
  }
  const url = buildTenantApiUrl(`${BASE_ENDPOINT}risk-register-attachment/`, "en");
  const cookie = (await import("react-cookies")).default;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRFToken": cookie.load("csrftoken") },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Upload failed");
  return data;
};

export const deleteRiskRegisterAttachment = async (attachmentId) => {
  return await fetchData(`${BASE_ENDPOINT}risk-register-attachment/${attachmentId}/`, {
    method: "DELETE",
  });
};

export const getRiskRegisterAttachments = async (riskId, riskType) => {
  return await fetchData(
    `${BASE_ENDPOINT}risk-register-attachments/?risk_id=${riskId}&risk_type=${riskType}`
  );
};

// ─── Vendor Review / Acknowledgement Flow ───

export const sendForVendorReview = async (data) => {
  return await fetchData(`${BASE_ENDPOINT}send-for-vendor-review/`, {
    method: "POST",
    body: data,
  });
};

export const getVendorReviewSubcategories = async (params = {}) => {
  return await fetchData(withQuery("vendor-review-sub-categories/", params));
};

export const getVendorReviewQuestionnaire = async (params = {}) => {
  return await fetchData(withQuery("vendor-review-questionnaire/", params));
};

export const flagVendorAnswer = async (data) => {
  const { body, query } = splitQueryPayload(data, ["review_token"]);
  return await fetchData(`${BASE_ENDPOINT}vendor-flag-answer/${query}`, {
    method: "PUT",
    body,
  });
};

export const submitVendorReview = async (data) => {
  const { body, query } = splitQueryPayload(data, ["review_token"]);
  return await fetchData(`${BASE_ENDPOINT}vendor-submit-review/${query}`, {
    method: "POST",
    body,
  });
};

export const getAssessmentFlaggedAnswers = async (assessmentId) => {
  return await fetchData(`${BASE_ENDPOINT}assessment-flagged-answers/?assessment_id=${assessmentId}`);
};

// ============================================================================
// Email Delivery Mode — Approver Review Flow
// ============================================================================

export const getApproverReviewSubcategories = async (params = {}) => {
  return await fetchData(withQuery("approver-review-sub-categories/", params));
};

export const getApproverReviewQuestionnaire = async (params = {}) => {
  return await fetchData(withQuery("approver-review-questionnaire/", params));
};

export const approverFlagAnswer = async (data) => {
  return await fetchData(`${BASE_ENDPOINT}approver-flag-answer/`, {
    method: "PUT",
    body: data,
  });
};

export const submitApproverReview = async (data) => {
  return await fetchData(`${BASE_ENDPOINT}approver-submit-review/`, {
    method: "POST",
    body: data,
  });
};

export const assignSection = async (sectionIds, userId) => {
  return await fetchData(`${BASE_ENDPOINT}assign-section/`, {
    method: "PUT",
    body: { section_ids: sectionIds, user_id: userId },
  });
};

export const getUserList = async () => {
  return await fetchData("user/userlist/?permission_code=SEAUAU");
};

// ─── Settings – Criticality Tier Configuration ───

export const getCriticalityTiers = async () => {
  return await fetchData(`${BASE_ENDPOINT}criticality-tiers/`);
};

export const saveCriticalityTiers = async (tiers) => {
  return await fetchData(`${BASE_ENDPOINT}criticality-tiers/save/`, {
    method: "POST",
    body: { tiers },
  });
};
